import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

/**
 * Card primitive — Studio Linen v1.0
 *
 * Variants:
 *   default      : 일반 컨테이너 (paper raised + hairline border + lg radius)
 *   feature      : Onboarding/landing 카드 (mono prefix + serif title + body)
 *   stat         : 메트릭 카드 (큰 serif 숫자 + mono 라벨)
 *   book         : 책 카드 (3/4 cover + page-edge + shelf) — LibraryPage 전용
 *   credential   : 인증서 카드 (5/7 paper + accent strip + ★ mark) — CredentialsPage 전용
 *
 * `interactive` — adds hover lift + cursor pointer + focus ring.
 *                 Use when the card itself is a link/button target.
 *
 * `padding`     — none | sm | md (default) | lg
 *
 * Compose with Card.Header / Card.Body / Card.Footer for structured layout.
 */
export type CardVariant = 'default' | 'feature' | 'stat' | 'book' | 'credential';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  interactive?: boolean;
  children?: ReactNode;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'default', padding = 'md', interactive = false, className, children, ...rest },
  ref,
) {
  const cn = [
    'bq-card',
    `bq-card--${variant}`,
    `bq-card--pad-${padding}`,
    interactive ? 'is-interactive' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={ref} className={cn} {...rest}>
      {children}
    </div>
  );
});

export interface CardSectionProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

const CardHeader = forwardRef<HTMLDivElement, CardSectionProps>(function CardHeader(
  { className, children, ...rest }, ref,
) {
  return (
    <div ref={ref} className={`bq-card__header ${className ?? ''}`} {...rest}>
      {children}
    </div>
  );
});

const CardBody = forwardRef<HTMLDivElement, CardSectionProps>(function CardBody(
  { className, children, ...rest }, ref,
) {
  return (
    <div ref={ref} className={`bq-card__body ${className ?? ''}`} {...rest}>
      {children}
    </div>
  );
});

const CardFooter = forwardRef<HTMLDivElement, CardSectionProps>(function CardFooter(
  { className, children, ...rest }, ref,
) {
  return (
    <div ref={ref} className={`bq-card__footer ${className ?? ''}`} {...rest}>
      {children}
    </div>
  );
});

// Attach subcomponents as static properties for `<Card.Header>` API.
type CardCompound = typeof Card & {
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};
(Card as CardCompound).Header = CardHeader;
(Card as CardCompound).Body = CardBody;
(Card as CardCompound).Footer = CardFooter;

export default Card as CardCompound;
export { CardHeader, CardBody, CardFooter };
